import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Modal,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { getScannedDocuments } from "_controllers/officer";
import AccessController from "_shared/AccessController";
import { DashGridL2 } from "layouts/dash-grid";
import TopBar from "_shared/Top";
import { Toaster } from "react-hot-toast";
import { toastMessage } from "utils/toastMessage";
import authAxios from "setups/axios";
import { useDispatch, useSelector } from "react-redux";
import { fetchOfficersList } from "slices/officers";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import AttachmentIcon from "@mui/icons-material/Attachment";
import axios from "axios";
import { schemeConstants } from "config/constants";
import * as XLSX from "xlsx";
import writeXlsxFile from "write-excel-file";

const messageStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "75%",
  maxHeight: "80vh",
  overflowY: "auto",
  bgcolor: "white",
  boxShadow: 24,
  p: 2,
  textAlign: "center",
};

const Main = () => {
  const dispatch = useDispatch();
  const { officers } = useSelector((state) => state.officers);
  const { user } = useSelector((state) => state.user);

  let sampleFile = `https://jkmsfa.s3.amazonaws.com/public/1713864902326-0.6080237631550627.xlsx`;

  useEffect(() => {
    if (!user?._id) return;
    dispatch(fetchOfficersList(user.SAP_ID));
  }, [user]);

  let updatedUsers = officers?.map((user) => ({
    SAP_ID: user?.SAP_ID,
    scanEnabled: user?.scanEnabled,
    areaName: user?.areaName,
    areaCode: user?.areaCode,
    regionName: user?.regionName,
    regionCode: user?.regionCode,
    name: user?.name,
    zone: user?.zone,
    territoryName: user?.territories[0]?.name,
    territoryCode: user?.territories[0]?.territoryCode,
  }));

  // console.log("updated", JSON.stringify(updatedUsers.slice(-3)));

  const [data, setData] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchUser, setSearchUser] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [image, setImage] = useState("");

  const [schemeData, setSchemeData] = useState();

  const [categories, setCategories] = useState([]);
  const [spendLimit, setSpendLimit] = useState([]);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [docListOpen, setDocListOpen] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");

  const userType = [
    { value: "SO", name: "Sales Officer" },
    { value: "AO", name: "Area Manager" },
    { value: "RO", name: "Regional Manager" },
    { value: "ZO", name: "Zonal Manager" },
  ];

  let columns = useMemo(() => [
    {
      headerName: "Filled Date",
      flex: 1,
    },
    {
      headerName: "Filled By",
      flex: 1,
    },
    // {
    //   headerName: "Uploaded On",
    //   flex: 2,
    // },
    {
      headerName: "#Files",
      flex: 2,
    },
    {
      headerName: "Action",
      flex: 1,
    },
  ]);

  useEffect(async () => {
    const survey = await getScannedDocuments({
      currentPage,
      searchUser,
    });
    setData(survey?.data);
    setMessage(survey?.message);
    setLoading(true);
    setTotalPages(survey?.totalPage ?? 1);
  }, [message, currentPage, searchUser]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const [formData, setFormData] = useState([]);

  const handleSubmit = async () => {
    const response = await authAxios()({
      url: `/api/v1/scan/user/access`,
      method: "PUT",
      data: selectedUsers,
    });
    toastMessage(response?.data?.message, "success");
  };

  useEffect(() => {
    let updatedCategories = categories.map((category) => {
      let roSpendLimit = spendLimit.find((obj) => obj.role === role);
      if (roSpendLimit) {
        let updatedSubCategory = category.subCategory.map((subCategory) => {
          let matchedSubCategory = roSpendLimit.spendLimit
            .find((s) => s.category === category._id)
            ?.subCategory.find((sub) => sub.subCategory === subCategory._id);
          if (matchedSubCategory) {
            return { ...subCategory, amount: matchedSubCategory.amount };
          }
          return subCategory;
        });
        return { ...category, subCategory: updatedSubCategory };
      }
      return category;
    });
    setCategories(updatedCategories);
  }, [role]);

  const handleViewClose = () => {
    setView(false);
    setDocListOpen(false);
  };

  const handleOpenClose = () => {
    setOpen(false);
  };

  const saveImg = async (e) => {
    setImage(e.target.files[0]);
  };

  const uploadDocument = async (e) => {
    e.preventDefault();
    if (!image) {
      // alert("Please select a file to upload.");
      setErrMsg("Please select a file to upload.");
      return;
    }
    const formData = new FormData();
    formData.append("file", image);
    formData.append("fileName", image.name);
    const token = localStorage.getItem("token");
    const config = {
      headers: {
        "Content-type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    const url = process.env.REACT_APP_REQUEST_ENDPOINT;
    await axios
      .put(`${url}/api/v1/scan/upload?id=${schemeData?._id}`, formData, config)
      .then((res) => {
        setMessage(res?.data?.message);
        toastMessage(res?.data?.message, "success");
        setUploadModal(false);
      })
      .catch((err) => {
        setImage("");
        setMessage(err?.message);
        toastMessage("Select File First.", "error");
        setUploadModal(false);
      });
  };

  const updatedString = (str) => {
    return Object?.entries(schemeConstants).reduce((acc, [key, value]) => {
      const regex = new RegExp(key, "gi");
      return acc.replace(regex, value);
    }, str);
  };

  const jsonToExcel = (json) => {
    console.log("working", JSON.parse(json));
    let fileName = "survey.xlsx";
    // const worksheet = XLSX.utils.json_to_sheet(json);
    // const workbook = XLSX.utils.book_new();
    // XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    // XLSX.writeFile(workbook, "data.xlsx");
    let excelData = [];

    for (const [key, value] of Object.entries(JSON.parse(json))) {
      if (typeof value.Rows === "object") {
        console.log("working2", key, value);
        excelData.push([
          {
            value: value?.Name,
          },
        ]);
        let head = [];
        for (let i = 0; i < value?.Headers.length; i++) {
          head.push({
            value: value?.Headers[i],
          });
        }
        excelData.push(head);
        for (let i = 0; i < value.Rows.length; i++) {
          let ar = [];
          for (let j = 0; j < value.Rows[i].length; j++) {
            ar.push({
              value: value.Rows[i][j],
            });
          }
          excelData.push(ar);
        }
      } else {
        console.log("working1");
        excelData.push([{ value: key }, { value }]);
      }
    }
    const downloadXlsxFile = () => {
      writeXlsxFile(excelData, {
        fileName: fileName,
      });
    };
    downloadXlsxFile();
  };

  const handleReload = () => {
    setMessage("Survey Updated");
  };

  console.log("view", image);

  console.log("updated", errMsg);

  return (
    <>
      <div
        style={{
          backgroundColor: "#F4F4F4",
          padding: "1rem",
          marginRight: "1rem",
          lineHeight: "2rem",
          borderRadius: "5px",
        }}
      >
        <div>
          <p
            style={{
              fontSize: "18px",
              fontWeight: "bolder",
              color: "#0E132F",
              fontWeight: "700",
              fontFamily: "Poppins",
            }}
          >
            Market Survey Reports - Reconciled
          </p>
        </div>
        <div
          style={{
            width: "100%",
            border: "1.5px solid grey",
          }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "end",
            margin: "1rem 0",
          }}
        >
          <Button variant="contained" onClick={() => window.open(sampleFile)}>
            Download Sample
          </Button>
        </div>

        <TextField
          onChange={(e) => setSearchUser(e.target.value)}
          value={searchUser}
          name="searchUser"
          variant="outlined"
          label="Search by SAP ID."
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <TableContainer
          component={Paper}
          sx={{
            maxHeight: "68vh",
            overflowY: "auto",
            marginTop: "1rem",
          }}
        >
          <Table>
            {columns && columns.length > 0 ? (
              <TableHead>
                <TableRow>
                  {columns.map((item, idx) => (
                    <TableCell
                      align={idx === 0 ? "left" : "center"}
                      sx={{ fontWeight: "bolder" }}
                      key={idx}
                    >
                      {item.headerName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            ) : null}

            <TableBody>
              {data && data?.length > 0 ? (
                data?.map((d) => (
                  <TableRow key={d?._id}>
                    <TableCell align="left">
                      {d?.documents
                        ? moment
                            .utc(d?.documents[0]?.scanDate)
                            .format("YYYY-MM-DD")
                        : "-"}
                    </TableCell>
                    <TableCell align="center">{d?._id ?? "-"}</TableCell>
                    {/* <TableCell align="center">
                      {d?.documentName ? updatedString(d?.documentName) : "-"}
                    </TableCell> */}
                    <TableCell align="center">
                      {d?.documents ? d?.documents?.length : 0}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        onClick={() => {
                          setDocListOpen(true);
                          setSchemeData(d);
                        }}
                      >
                        {/* <VisibilityIcon
                            sx={{
                              border: "1px solid darkgrey",
                              fontSize: "32px",
                              borderRadius: "3px",
                              cursor: "pointer",
                              padding: "0 5px",
                            }}
                            onClick={() => {
                              setView(true);
                              setSchemeData(d);
                            }}
                          /> */}
                        Upload
                      </Button>
                      {/* <Button>
                          <DownloadIcon
                            sx={{
                              border: "1px solid darkgrey",
                              fontSize: "32px",
                              borderRadius: "3px",
                              cursor: "pointer",
                              padding: "0 5px",
                            }}
                            onClick={() => {
                              jsonToExcel(d?.document);
                            }}
                          />
                        </Button> */}
                    </TableCell>
                    {/* <TableCell align="left">
                        <Button>
                          <FileUploadIcon
                            sx={{
                              border: "1px solid darkgrey",
                              fontSize: "32px",
                              borderRadius: "3px",
                              cursor: "pointer",
                              padding: "0 5px",
                            }}
                            onClick={() => {
                              setUploadModal(true);
                              setSchemeData(d);
                            }}
                          />
                        </Button>
                        <Button
                          disabled={
                            d?.uploadedDocument === null ||
                            d?.uploadedDocument === undefined
                              ? true
                              : false
                          }
                        >
                          <a
                            href={d?.uploadedDocument}
                            target="_blank"
                            download
                            style={{
                              textDecoration: "none",
                            }}
                          >
                            <DownloadIcon
                              sx={{
                                border: "1px solid darkgrey",
                                fontSize: "32px",
                                borderRadius: "3px",
                                cursor: "pointer",
                                padding: "0 5px",
                                marginTop: "8px",
                              }}
                            />
                          </a>
                        </Button>
                        <Button>
                          <AttachmentIcon
                            sx={{
                              border: "1px solid darkgrey",
                              fontSize: "32px",
                              borderRadius: "3px",
                              cursor: "pointer",
                              // marginLeft: "1rem",
                              padding: "0 5px",
                            }}
                            onClick={() => {
                              setView(true);
                              setSchemeData(d);
                            }}
                          />
                        </Button>
                      </TableCell> */}
                  </TableRow>
                ))
              ) : (
                <Box sx={{ textAlign: "left", pt: 1 }}>
                  <Typography variant="h6" sx={{ p: 1 }}>
                    No Data.
                  </Typography>
                </Box>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Grid justifyContent={"center"} container p={1}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Grid>
      </div>

      {/* {view ? (
        <SchemeView
          view={view}
          handleViewClose={handleViewClose}
          data={schemeData}
          handleReload={handleReload}
        />
      ) : null} */}

      <Modal
        open={uploadModal}
        onClose={() => {
          setUploadModal(false);
          setErrMsg("");
        }}
      >
        <Box
          sx={{
            width: "40%",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            border: "none",
            boxShadow: 24,
            p: "2rem",
            textAlign: "left",
            letterSpacing: 1,
            borderRadius: "8px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            <p
              style={{
                fontFamily: "Inter",
                fontSize: "18px",
                fontWeight: "500",
                lineHeight: "24px",
                textAlign: "left",
                fontFamily: "Public Sans",
                // padding: "1rem 0",
              }}
            >
              Please upload modified survey file.
            </p>
            <TextField
              onChange={saveImg}
              InputProps={{
                disableUnderline: true,
              }}
              type={"file"}
              id="standard-basic"
              label="Image"
              name="image"
              variant="standard"
              style={{ border: "0" }}
              error={errMsg !== "" ? true : false}
              helperText={errMsg !== "" ? errMsg : ""}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "1.5rem",
                height: "2.5rem",
              }}
            >
              <button
                style={{
                  backgroundColor: "rgba(168, 170, 174, 0.16)",
                  padding: "10px 30px",
                  borderRadius: "6px",
                  fontWeight: "500",
                  color: "#A8AAAE",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setUploadModal(false);
                  setImage("");
                  setErrMsg("");
                }}
              >
                Cancel
              </button>
              <button
                style={{
                  backgroundColor: "#3273F6",
                  padding: "10px 30px",
                  borderRadius: "6px",
                  fontWeight: "500",
                  color: "#ffffff",
                  marginLeft: "1rem",
                  cursor: "pointer",
                }}
                onClick={uploadDocument}
              >
                Submit
              </button>
            </div>
          </div>
        </Box>
      </Modal>

      <Modal
        open={docListOpen}
        onClose={() => {
          setDocListOpen(false);
        }}
      >
        <Box
          sx={{
            width: "75%",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            border: "none",
            boxShadow: 24,
            p: "2rem",
            textAlign: "left",
            letterSpacing: 1,
            borderRadius: "8px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            <p
              style={{
                fontFamily: "Inter",
                fontSize: "24px",
                fontWeight: "600",
                lineHeight: "24px",
                textAlign: "center",
                fontFamily: "Public Sans",
              }}
            >
              Upload and View Survey Files.
            </p>
            <button
              style={{
                position: "fixed",
                top: "15px",
                right: "30px",
                border: "2px solid darkgrey",
                padding: "15px",
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "3px",
                color: "red",
                fontWeight: "600",
                fontSize: "24px",
                cursor: "pointer",
              }}
              onClick={() => setDocListOpen(false)}
            >
              X
            </button>
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: "70vh",
                overflowY: "auto",
                marginTop: "2rem",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="left" sx={{ fontWeight: "bolder" }}>
                      S.No
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bolder" }}>
                      File Name
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bolder" }}>
                      Uploaded On
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bolder" }}>
                      Actions
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bolder" }}>
                      Downloaded On
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {schemeData?.documents &&
                  schemeData?.documents?.length > 0 ? (
                    schemeData?.documents?.map((d, idx) => (
                      <TableRow key={d?._id}>
                        <TableCell align="left">{idx + 1}</TableCell>
                        <TableCell align="center">
                          {updatedString(d?.documentName) ?? "-"}
                        </TableCell>
                        <TableCell align="center">
                          {moment.utc(d?.scanDate).format("YYYY-MM-DD") ?? "-"}
                        </TableCell>
                        {/* <TableCell align="center">
                          <Button>
                            <VisibilityIcon
                              sx={{
                                border: "1px solid darkgrey",
                                fontSize: "32px",
                                borderRadius: "3px",
                                cursor: "pointer",
                                padding: "0 5px",
                              }}
                              onClick={() => {
                                setView(true);
                                setSchemeData(d);
                              }}
                            />
                          </Button>
                          <Button>
                            <DownloadIcon
                              sx={{
                                border: "1px solid darkgrey",
                                fontSize: "32px",
                                borderRadius: "3px",
                                cursor: "pointer",
                                padding: "0 5px",
                              }}
                              onClick={() => {
                                jsonToExcel(d?.document);
                              }}
                            />
                          </Button>
                        </TableCell> */}

                        <TableCell align="center">
                          <Button>
                            <FileUploadIcon
                              sx={{
                                border: "1px solid darkgrey",
                                fontSize: "32px",
                                borderRadius: "3px",
                                cursor: "pointer",
                                padding: "0 5px",
                              }}
                              onClick={() => {
                                setUploadModal(true);
                                setDocListOpen(false);
                                setSchemeData(d);
                              }}
                            />
                          </Button>
                          <Button
                            disabled={
                              d?.uploadedDocument === null ||
                              d?.uploadedDocument === undefined
                                ? true
                                : false
                            }
                          >
                            <a
                              href={d?.uploadedDocument}
                              target="_blank"
                              download
                              style={{
                                textDecoration: "none",
                              }}
                            >
                              <DownloadIcon
                                sx={{
                                  border: "1px solid darkgrey",
                                  fontSize: "32px",
                                  borderRadius: "3px",
                                  cursor: "pointer",
                                  padding: "0 5px",
                                  marginTop: "8px",
                                }}
                              />
                            </a>
                          </Button>
                        </TableCell>
                        <TableCell align="center">
                          {moment.utc(d?.scanDate).format("YYYY-MM-DD") ?? "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <Box sx={{ textAlign: "left", pt: 1 }}>
                      <Typography variant="h6" sx={{ p: 1 }}>
                        No Data.
                      </Typography>
                    </Box>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Box>
      </Modal>
    </>
  );
};

const SurevyReconcilation = () => {
  return (
    <AccessController roles={["ADMIN", "AO"]}>
      <DashGridL2 Top={TopBar} Main={Main} />
      <Toaster />
    </AccessController>
  );
};

export default SurevyReconcilation;
